import { CommsCheckResult, DualFuelCommsCheckResult, XrdlgResponseData, XrinvResponseData } from '../../types/comms-check';
import { runXrdlgCheck, runXrinvCheck, runXritmCheck } from './checkRunners';
import { mergeXrdlgIntoXrinv } from './util';

export interface DualFuelCommsCheckInputs {
  mpan: string;
  mprn: string;
  elecMsn: string;
  gasMsn: string;
}

/**
 * Dual fuel comms check (MVS-12049): 1x XRINV + 2x XRITM in parallel, then XRDLG
 * chained off XRINV's CHF guid. Wait-for-all: no check aborts another, each check
 * reports its own final state. Never rejects.
 */
export async function runDualFuelCommsCheck(
  inputs: DualFuelCommsCheckInputs,
  signal?: AbortSignal,
): Promise<DualFuelCommsCheckResult> {
  const elecXritmPromise = runXritmCheck(inputs.mpan, inputs.elecMsn, signal);
  const gasXritmPromise = runXritmCheck(inputs.mprn, inputs.gasMsn, signal);

  const rawXrinv = await runXrinvCheck(inputs.mpan, signal);
  const rawXrdlg = await runChainedXrdlgCheck(inputs.mpan, rawXrinv, signal);
  const { xrinv, xrdlg } = mergeDeviceLogIntoInventory(rawXrinv, rawXrdlg);

  return {
    xrinv,
    elecXritm: await elecXritmPromise,
    gasXritm: await gasXritmPromise,
    xrdlg,
  };
}

/** XRDLG needs the inventory's CHF guid, so it is NOT_STARTED unless XRINV succeeded. */
async function runChainedXrdlgCheck(mpan: string, xrinv: XrinvResponseData, signal?: AbortSignal): Promise<XrdlgResponseData> {
  if (xrinv.state !== 'SUCCESS') {
    return { state: 'NOT_STARTED', data: [] };
  }
  const chfGuid = xrinv.data.find((device) => device.deviceType === 'CHF')?.deviceId;
  return runXrdlgCheck(mpan, chfGuid, signal);
}

/**
 * Same post-processing as the single fuel CommsCheck: on XRDLG success the inventory
 * devices gain last communicated time / active status from the device log, and the
 * device log keeps only the devices that are NOT in the inventory ("other devices").
 */
function mergeDeviceLogIntoInventory(
  xrinv: XrinvResponseData,
  xrdlg: XrdlgResponseData,
): { xrinv: XrinvResponseData; xrdlg: XrdlgResponseData } {
  if (xrdlg.state !== 'SUCCESS') {
    return { xrinv, xrdlg };
  }
  const inventoryDeviceIds = xrinv.data.map((device) => device.deviceId);
  return {
    xrinv: { ...xrinv, data: mergeXrdlgIntoXrinv(xrinv.data, xrdlg.data) },
    xrdlg: { ...xrdlg, data: xrdlg.data.filter((device) => !inventoryDeviceIds.includes(device.deviceId)) },
  };
}

export interface SingleFuelReportingView {
  result: CommsCheckResult;
  isMeterActive: boolean;
}

export interface DualFuelReportingViews {
  elec: SingleFuelReportingView;
  gas: SingleFuelReportingView;
}

/**
 * The dual fuel check is reported as two single-fuel comms checks, one per meter,
 * so each report matches what the existing /comms-check reporting rows contain.
 * XRINV and XRDLG ran once (against the MPAN) but cover the shared comms hub, so
 * both views carry them; only the XRITM and the meter's active status differ.
 */
export function toSingleFuelReportingViews(result: DualFuelCommsCheckResult): DualFuelReportingViews {
  const isMeterActive = (deviceType: 'ESME' | 'GSME') =>
    result.xrinv.data.find((device) => device.deviceType === deviceType)?.activeStatus === true;

  return {
    elec: {
      result: { xrinv: result.xrinv, xritm: result.elecXritm, xrdlg: result.xrdlg },
      isMeterActive: isMeterActive('ESME'),
    },
    gas: {
      result: { xrinv: result.xrinv, xritm: result.gasXritm, xrdlg: result.xrdlg },
      isMeterActive: isMeterActive('GSME'),
    },
  };
}

/**
 * Happy path (MVS-12050): every check succeeded, both meters are in comms and the
 * device log found no devices outside the inventory ("other devices" must go via SST).
 * Also requires the data the results screen renders (address, MSNs, readings) — a
 * SUCCESS result with any of them missing must not reach the Confirm Site Details screen.
 */
export function isDualFuelHappyPath(result: DualFuelCommsCheckResult): boolean {
  const allSucceeded =
    result.xrinv.state === 'SUCCESS' &&
    result.elecXritm.state === 'SUCCESS' &&
    result.gasXritm.state === 'SUCCESS' &&
    result.xrdlg.state === 'SUCCESS';
  const esme = result.xrinv.data.find((device) => device.deviceType === 'ESME');
  const gsme = result.xrinv.data.find((device) => device.deviceType === 'GSME');
  const hasConfirmableData =
    !!result.xrinv.address &&
    !!result.elecXritm.msn &&
    result.elecXritm.data.length > 0 &&
    !!result.gasXritm.msn &&
    result.gasXritm.data.length > 0;

  return (
    allSucceeded &&
    esme?.activeStatus === true &&
    gsme?.activeStatus === true &&
    result.xrdlg.data.length === 0 &&
    hasConfirmableData
  );
}
